/**
 * Менеджерское управление меню (CRUD) — клиент админ-каталога услуг.
 *
 * Меню = F&B-категории каталога услуг, поэтому менеджер/владелец управляет
 * ими через тот же admin-CRUD каталога: /v1/admin/service-catalog/*.
 *
 * ВАЖНО (сверено с живым бэком oblaka 2026-06-16): backend для обновления
 * принимает PUT, а удаление — DELETE; общий @hsc/api-client использует
 * PATCH (→ 405) и не имеет delete, поэтому здесь — отдельные обёртки с
 * правильными глаголами. Тело PUT — ПОЛНАЯ форма (категория/позиция),
 * как ожидает handler (toUpdate() читает все поля).
 *
 * RBAC: гейтится бэком (platform_admin | hotel_manager | tenant_owner).
 */
import { customFetch } from '@hsc/api-client';
import type {
  LocalizedText,
  PriceMode,
  ServiceExecutor,
} from '@hsc/types';

const BASE = '/admin/service-catalog';

/** Полная админ-проекция категории (snake_case как в DTO бэка). */
export interface AdminCategory {
  id: string;
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  department_code: string;
  sla_response_min?: number;
  sla_resolution_min?: number;
  /** Ключ иконки категории (свободная строка; таблицы icons нет). */
  icon?: string;
  /** Обложка категории — СЫРОЕ хранимое значение (object-ключ MinIO / URL). */
  cover_url?: string;
  /** Окно доступности "HH:MM-HH:MM" или пусто (весь день / часы ресторана). */
  availability?: string;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
}

/** Полная админ-проекция позиции меню. */
export interface AdminItem {
  id: string;
  category_id: string;
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  is_paid: boolean;
  price_mode: PriceMode;
  price_minor?: number;
  currency?: string;
  show_price: boolean;
  executor?: ServiceExecutor;
  external_url?: string;
  /** Фото позиции — СЫРОЕ хранимое значение (object-ключ MinIO / URL); его
   *  форма пере-отправляет без изменений. */
  image_url?: string;
  /** Превью-URL фото (presigned GET); только для показа, не отправлять. */
  image_preview_url?: string;
  /** Себестоимость в минорных единицах (валюта = currency); нет → undefined. */
  cost_minor?: number;
  /** Теги аллергенов/диет (veg/gluten/lactose/spicy/…); всегда массив. */
  allergens?: string[];
  is_active: boolean;
  sort_order: number;
}

/** Тело создания/полного обновления категории (PUT/POST). */
export interface CategoryBody {
  code?: string; // только на создание; на PUT игнорируется (immutable)
  name: LocalizedText;
  description?: LocalizedText;
  department_code: string;
  sla_response_min?: number;
  sla_resolution_min?: number;
  /** Ключ иконки (опц.). */
  icon?: string;
  /** Обложка: object-ключ MinIO из presign-загрузки или http(s)-URL (опц.). */
  cover_url?: string;
  /** Окно доступности "HH:MM-HH:MM" или "" (весь день). */
  availability?: string;
  sort_order: number;
}

/** Тело создания/полного обновления позиции (PUT/POST). */
export interface ItemBody {
  category_id?: string; // только на создание; на PUT игнорируется (immutable)
  code?: string; // только на создание
  name: LocalizedText;
  description?: LocalizedText;
  is_paid: boolean;
  price_mode: PriceMode;
  price_minor?: number | null;
  currency?: string | null;
  show_price: boolean;
  /**
   * Фото позиции — object-ключ MinIO из presign-загрузки (/v1/files) или
   * абсолютный http(s)-URL. null/пусто — фото убрать/нет.
   */
  image_url?: string | null;
  /** Себестоимость в минорных единицах; null/undefined — не указана. */
  cost_minor?: number | null;
  /** Теги аллергенов/диет; пустой массив — тегов нет. */
  allergens?: string[];
  sort_order: number;
}

/* ===================== Категории ===================== */

export async function listAdminCategories(): Promise<AdminCategory[]> {
  const raw = await customFetch<{ categories?: AdminCategory[]; total?: number }>({
    url: `${BASE}/categories`,
    method: 'GET',
  });
  return raw.categories ?? [];
}

export async function createAdminCategory(body: CategoryBody): Promise<AdminCategory> {
  return customFetch<AdminCategory>({ url: `${BASE}/categories`, method: 'POST', data: body });
}

export async function updateAdminCategory(id: string, body: CategoryBody): Promise<AdminCategory> {
  return customFetch<AdminCategory>({
    url: `${BASE}/categories/${encodeURIComponent(id)}`,
    method: 'PUT',
    data: body,
  });
}

export async function deleteAdminCategory(id: string): Promise<void> {
  await customFetch<void>({
    url: `${BASE}/categories/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}

export async function setAdminCategoryActive(id: string, active: boolean): Promise<AdminCategory> {
  return customFetch<AdminCategory>({
    url: `${BASE}/categories/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`,
    method: 'POST',
  });
}

/* ===================== Позиции ===================== */

export async function listAdminItems(categoryId: string): Promise<AdminItem[]> {
  const raw = await customFetch<{ items?: AdminItem[]; total?: number }>({
    url: `${BASE}/items`,
    method: 'GET',
    params: { category_id: categoryId },
  });
  return raw.items ?? [];
}

export async function createAdminItem(body: ItemBody): Promise<AdminItem> {
  return customFetch<AdminItem>({ url: `${BASE}/items`, method: 'POST', data: body });
}

export async function updateAdminItem(id: string, body: ItemBody): Promise<AdminItem> {
  return customFetch<AdminItem>({
    url: `${BASE}/items/${encodeURIComponent(id)}`,
    method: 'PUT',
    data: body,
  });
}

export async function deleteAdminItem(id: string): Promise<void> {
  await customFetch<void>({ url: `${BASE}/items/${encodeURIComponent(id)}`, method: 'DELETE' });
}

export async function setAdminItemActive(id: string, active: boolean): Promise<AdminItem> {
  return customFetch<AdminItem>({
    url: `${BASE}/items/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`,
    method: 'POST',
  });
}
